// API utility functions for fetching products

export interface Product {
  id: number;
  external_id: string;
  title: string;
  description: string;
  brand: string;
  category: string;
  material: string;
  price: number;
  old_price?: number;
  discount?: number;
  currency: string;
  image_url?: string;
  product_url: string;
  shop: string;
  in_stock: boolean;
  stock_count?: number;
  sku?: string;
  keywords: string[];
  path: string;
  feed_source: string;
  created_at: string;
  updated_at: string;
}

export interface ProductsResponse {
  success: boolean;
  data: {
    products: Product[];
    pagination?: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    filters?: {
      category?: string;
      brand?: string;
      search?: string;
      sort?: string;
    };
  };
}

export interface ProductResponse {
  success: boolean;
  data: {
    product: Product;
  };
}

const API_BASE_URL = '/api';

export async function fetchProducts(params: {
  page?: number;
  limit?: number;
  category?: string;
  brand?: string;
  search?: string;
  sort?: string;
} = {}): Promise<ProductsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.category) queryParams.append('category', params.category);
  if (params.brand) queryParams.append('brand', params.brand);
  if (params.search) queryParams.append('search', params.search);
  if (params.sort) queryParams.append('sort', params.sort);

  const response = await fetch(`${API_BASE_URL}/products?${queryParams.toString()}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch products: ${response.statusText}`);
  }
  
  return response.json();
}

// New function for load more functionality
export async function fetchMoreProducts(params: {
  page: number;
  limit: number;
  category?: string;
  brand?: string;
  search?: string;
  sort?: string;
}): Promise<{ products: Product[]; hasMore: boolean; totalCount: number }> {
  const response = await fetchProducts(params);
  
  if (!response.success) {
    throw new Error('Failed to fetch more products');
  }
  
  const { products, pagination } = response.data;
  
  return {
    products,
    hasMore: pagination?.hasNext ?? false,
    totalCount: pagination?.totalCount ?? 0,
  };
}

export async function fetchFeaturedProducts(limit: number = 8): Promise<ProductsResponse> {
  const response = await fetch(`${API_BASE_URL}/products/featured?limit=${limit}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch featured products: ${response.statusText}`);
  }
  
  return response.json();
}

export async function fetchProduct(idOrSlug: string | number): Promise<ProductResponse> {
  const response = await fetch(`${API_BASE_URL}/products/${encodeURIComponent(String(idOrSlug))}`);
  
  // Check content type before parsing
  const contentType = response.headers.get('content-type') || '';
  
  // Clone response to read as text first if needed, then parse as JSON
  const clonedResponse = response.clone();
  
  if (!contentType.includes('application/json')) {
    // Response is not JSON, likely HTML error page
    const text = await clonedResponse.text();
    console.error(`API endpoint /products/${idOrSlug} returned non-JSON (${contentType}):`, text.substring(0, 500));
    throw new Error(`API endpoint not found. The serverless function may not be deployed. Status: ${response.status}`);
  }
  
  if (!response.ok) {
    // Try to parse error response as JSON
    let errorMessage = `Failed to fetch product: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // If JSON parsing fails, use the default error message
    }
    throw new Error(errorMessage);
  }
  
  return response.json();
}

export async function fetchCategories(): Promise<{ success: boolean; data: { categories: { category: string; count: number }[] } }> {
  const response = await fetch(`${API_BASE_URL}/categories`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }
  
  return response.json();
}

export async function fetchBrands(): Promise<{ success: boolean; data: { brands: { brand: string; count: number }[] } }> {
  const response = await fetch(`${API_BASE_URL}/brands`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch brands: ${response.statusText}`);
  }
  
  return response.json();
}

// Helper function to format price in Danish Kroner
export function formatPrice(price: number, currency: string = 'DKK'): string {
  if (currency === 'DKK') {
    return `${price.toLocaleString('da-DK')} kr.`;
  }
  return `${price.toLocaleString('da-DK')} ${currency}`;
}

// Helper function to calculate discount percentage
export function calculateDiscountPercentage(price: number, oldPrice: number): number {
  if (!oldPrice || oldPrice <= price) return 0;
  return Math.round(((oldPrice - price) / oldPrice) * 100);
}

